import { Ban, Check, SkipForward } from "lucide-react";
import { Button } from "@/modern/components/ui/button";
import { cn } from "@/modern/utils";
import type { ActionFlags } from "../utils/actionMatrix";

type Props = {
  actions: ActionFlags;
  busy?: boolean;
  /** Defaults to "Skip"; use "To Skip" on Accepted/Rejected tabs. */
  skipLabel?: string;
  onAccept: () => void;
  onReject: () => void;
  onSkip: () => void;
};

const chip =
  "h-8 shrink-0 gap-1 rounded-full px-2.5 text-xs font-semibold shadow-none [&_svg]:size-3.5";

/** Header bulk actions — Select/Clear live beside Reanalyze; Reprocess stays in the detail sheet. */
export default function PromoteSurfaceSelectionBar({
  actions,
  busy,
  skipLabel = "Skip",
  onAccept,
  onReject,
  onSkip,
}: Props) {
  return (
    <div className="flex min-w-0 items-center justify-end gap-1 overflow-x-auto sm:gap-1.5">
      {actions.accept && (
        <Button size="sm" className={cn(chip)} disabled={busy} onClick={onAccept}>
          <Check aria-hidden strokeWidth={2.5} />
          Accept
        </Button>
      )}
      {actions.reject && (
        <Button
          size="sm"
          variant="ghost"
          className={cn(
            chip,
            "bg-destructive/20 text-destructive hover:bg-destructive/30 hover:text-destructive"
          )}
          disabled={busy}
          onClick={onReject}
        >
          <Ban aria-hidden />
          <span className="hidden sm:inline">Reject</span>
        </Button>
      )}
      {actions.skip && (
        <Button
          size="sm"
          variant="ghost"
          className={cn(chip, "bg-muted/80 text-foreground hover:bg-muted")}
          disabled={busy}
          onClick={onSkip}
        >
          <SkipForward aria-hidden />
          <span className="hidden sm:inline">{skipLabel}</span>
        </Button>
      )}
    </div>
  );
}
